import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LangContext";
import Navbar from "../components/Navbar";

const API = process.env.REACT_APP_API_URL || "http://localhost:3001";

export default function Admin() {
  const { getToken } = useAuth();
  const { lang } = useLang();
  const L = lang.admin;
  const [activeNav, setActiveNav] = useState("stats");
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [reports, setReports] = useState([]);
  const [revenue, setRevenue] = useState(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  const get = useCallback(async (path) => {
    const res = await fetch(`${API}${path}`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    return res.json();
  }, [getToken]);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try { setStats(await get("/api/admin/stats")); }
    catch { } finally { setLoading(false); }
  }, [get]);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const data = await get(`/api/admin/users?search=${search}`);
      setUsers(data.users || []);
    } catch { } finally { setLoading(false); }
  }, [get, search]);

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const data = await get("/api/admin/reports?limit=50");
      setReports(data.reports || []);
    } catch { } finally { setLoading(false); }
  }, [get]);

  const fetchRevenue = useCallback(async () => {
    setLoading(true);
    try { setRevenue(await get("/api/admin/revenue")); }
    catch { } finally { setLoading(false); }
  }, [get]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    if (activeNav === "stats" && !stats) fetchStats();
    if (activeNav === "users") fetchUsers();
    if (activeNav === "reports") fetchReports();
    if (activeNav === "revenue" && !revenue) fetchRevenue();
  }, [activeNav, stats, revenue, fetchStats, fetchUsers, fetchReports, fetchRevenue]);

  const toggleAdmin = async (userId, currentRole) => {
    const newRole = currentRole === "admin" ? "user" : "admin";
    await fetch(`${API}/api/admin/users/${userId}`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${getToken()}`,
      },
      body: JSON.stringify({ role: newRole }),
    });
    fetchUsers();
  };

  const NAV = [
    { id: "stats",   label: L.navStats },
    { id: "users",   label: L.navUsers },
    { id: "reports", label: L.navReports },
    { id: "revenue", label: L.navRevenue },
  ];

  const KPI = stats ? [
    { label: L.statUsers,      value: stats.total_users },
    { label: L.statReports,    value: stats.total_reports },
    { label: L.statRevenue,    value: `${stats.total_revenue} RON` },
    { label: L.statActiveSubs, value: stats.active_subs },
    { label: L.statToday,      value: stats.reports_today },
    { label: L.statMonth,      value: `${stats.revenue_month} RON` },
  ] : [];

  return (
    <div style={{ minHeight: "100vh", background: "#f8fafc" }}>
      <Navbar />
      <div style={{ maxWidth: 920, margin: "0 auto", padding: "24px 16px" }}>

        <h1 style={{ fontSize: 20, fontWeight: 700, margin: "0 0 20px", fontFamily: "Georgia, serif" }}>{L.title}</h1>

        <div style={{ display: "flex", gap: 2, marginBottom: 24, borderBottom: "0.5px solid #e5e7eb" }}>
          {NAV.map(n => (
            <button key={n.id} onClick={() => setActiveNav(n.id)}
              style={{ padding: "8px 16px", background: "none", border: "none", borderBottom: activeNav === n.id ? "2px solid #1d4ed8" : "2px solid transparent", color: activeNav === n.id ? "#1d4ed8" : "#6b7280", fontSize: 13, fontWeight: activeNav === n.id ? 600 : 400, cursor: "pointer", fontFamily: "sans-serif" }}>
              {n.label}
            </button>
          ))}
        </div>

        {loading && <p style={{ fontSize: 13, color: "#9ca3af", fontFamily: "sans-serif" }}>{lang.common.loading}</p>}

        {activeNav === "stats" && stats && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
            {KPI.map(k => (
              <div key={k.label} style={{ background: "#fff", border: "0.5px solid #e5e7eb", borderRadius: 10, padding: "16px 18px" }}>
                <p style={{ fontSize: 11, color: "#9ca3af", margin: "0 0 4px", fontFamily: "sans-serif", textTransform: "uppercase", letterSpacing: 0.5 }}>{k.label}</p>
                <p style={{ fontSize: 24, fontWeight: 700, color: "#1d4ed8", margin: 0, fontFamily: "sans-serif" }}>{k.value}</p>
              </div>
            ))}
          </div>
        )}

        {activeNav === "users" && (
          <>
            <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
              <input value={search} onChange={e => setSearch(e.target.value)}
                placeholder={L.searchUsers}
                style={{ flex: 1, padding: "8px 12px", border: "0.5px solid #d1d5db", borderRadius: 8, fontSize: 13, fontFamily: "sans-serif" }} />
              <button onClick={fetchUsers}
                style={{ padding: "8px 16px", background: "#1d4ed8", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, cursor: "pointer", fontFamily: "sans-serif" }}>
                {lang.common.retry}
              </button>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {users.map(u => (
                <div key={u.id} style={{ background: "#fff", border: "0.5px solid #e5e7eb", borderRadius: 10, padding: "14px 16px", display: "flex", alignItems: "center", gap: 16 }}>
                  <div style={{ flex: 1 }}>
                    <p style={{ fontSize: 14, fontWeight: 500, margin: "0 0 2px", fontFamily: "sans-serif" }}>{u.email}</p>
                    <p style={{ fontSize: 12, color: "#9ca3af", margin: 0, fontFamily: "sans-serif" }}>
                      {u.name || lang.common.unknown} · {u.role} · {u.total_reports} rapoarte · {u.total_spent} RON
                    </p>
                  </div>
                  <div style={{ display: "flex", gap: 6 }}>
                    <span style={{ fontSize: 11, background: u.role === "admin" ? "#dbeafe" : "#f3f4f6", color: u.role === "admin" ? "#1e40af" : "#6b7280", padding: "2px 8px", borderRadius: 8, fontFamily: "sans-serif" }}>
                      {u.role}
                    </span>
                    <button onClick={() => toggleAdmin(u.id, u.role)}
                      style={{ padding: "4px 10px", background: "#f9fafb", border: "0.5px solid #e5e7eb", borderRadius: 6, fontSize: 11, cursor: "pointer", fontFamily: "sans-serif" }}>
                      {u.role === "admin" ? L.removeAdmin : L.makeAdmin}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {activeNav === "reports" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {reports.map(r => (
              <div key={r.id} style={{ background: "#fff", border: "0.5px solid #e5e7eb", borderRadius: 10, padding: "12px 16px", display: "flex", alignItems: "center", gap: 16 }}>
                <div style={{ width: 44, textAlign: "center", flexShrink: 0 }}>
                  <p style={{ fontSize: 18, fontWeight: 700, color: "#1d4ed8", margin: 0, fontFamily: "sans-serif" }}>{Math.round(r.feur_score)}</p>
                  <p style={{ fontSize: 10, color: "#9ca3af", margin: 0, fontFamily: "sans-serif" }}>FEUR</p>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: 13, fontWeight: 600, margin: "0 0 2px", fontFamily: "sans-serif" }}>{r.title}</p>
                  <p style={{ fontSize: 11, color: "#9ca3af", margin: 0, fontFamily: "sans-serif" }}>
                    {r.user_email} · {r.genre_label} · {r.tier} · {new Date(r.created_at).toLocaleDateString("ro-RO")}
                  </p>
                </div>
                <span style={{ fontSize: 11, background: "#f3f4f6", color: "#6b7280", padding: "2px 8px", borderRadius: 8, fontFamily: "sans-serif" }}>
                  {r.verdict}
                </span>
              </div>
            ))}
          </div>
        )}

        {activeNav === "revenue" && revenue && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <div style={{ background: "#fff", border: "0.5px solid #e5e7eb", borderRadius: 10, padding: 18 }}>
              <p style={{ fontSize: 13, fontWeight: 600, margin: "0 0 14px", fontFamily: "sans-serif" }}>Pe tier</p>
              {(revenue.byTier || []).map(t => (
                <div key={t.tier} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: "0.5px solid #f3f4f6" }}>
                  <span style={{ fontSize: 13, fontFamily: "sans-serif" }}>{t.tier}</span>
                  <span style={{ fontSize: 13, fontWeight: 600, fontFamily: "sans-serif" }}>{t.total} RON ({t.count})</span>
                </div>
              ))}
            </div>
            <div style={{ background: "#fff", border: "0.5px solid #e5e7eb", borderRadius: 10, padding: 18 }}>
              <p style={{ fontSize: 13, fontWeight: 600, margin: "0 0 14px", fontFamily: "sans-serif" }}>Pe lună</p>
              {(revenue.byMonth || []).map(m => (
                <div key={m.month} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: "0.5px solid #f3f4f6" }}>
                  <span style={{ fontSize: 13, fontFamily: "sans-serif" }}>{m.month}</span>
                  <span style={{ fontSize: 13, fontWeight: 600, fontFamily: "sans-serif" }}>{m.total} RON ({m.count})</span>
                </div>
              ))}
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
